export type SubtitleSegment = { text: string; isWordLike: boolean };

export type SubtitleSegmentWithOffsets = SubtitleSegment & {
  start: number;
  end: number;
};

export type SubtitleHtmlSegment = SubtitleSegment & {
  plain: string;
  html: string;
};

/** Strip subtitle HTML (ruby readings, tags) into plain text for Intl.Segmenter. */
export function toPlainSubtitleText(html: string | null | undefined): string {
  if (html == null) return '';
  let text = String(html);
  text = text.replace(/<br\s*\/?>/gi, '\n');
  text = text.replace(/<rt\b[^>]*>[\s\S]*?<\/rt>/gi, '');
  text = text.replace(/<rp\b[^>]*>[\s\S]*?<\/rp>/gi, '');
  text = text.replace(/<[^>]+>/g, '');
  return text
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/&lrm;/gi, '');
}

function escapeHtml(text: string): string {
  return String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function createSegmenter(locale: string | undefined | null): Intl.Segmenter | null {
  const Segmenter = globalThis.Intl?.Segmenter;
  if (typeof Segmenter !== 'function') return null;
  try {
    if (locale) return new Segmenter(locale, { granularity: 'word' });
  } catch {
    // fall through
  }
  try {
    return new Segmenter(undefined, { granularity: 'word' });
  } catch {
    try {
      return new Segmenter('en', { granularity: 'word' });
    } catch {
      return null;
    }
  }
}

function fallbackSegments(text: string): SubtitleSegmentWithOffsets[] {
  const segments: SubtitleSegmentWithOffsets[] = [];
  const re = /(\s+)|(\S+)/g;
  let match = re.exec(text);
  while (match) {
    if (match[1] != null) {
      segments.push({ text: match[1], isWordLike: false, start: match.index, end: match.index + match[1].length });
    } else if (match[2] != null) {
      segments.push({ text: match[2], isWordLike: true, start: match.index, end: match.index + match[2].length });
    }
    match = re.exec(text);
  }
  return segments;
}

export function segmentSubtitleText(
  text: string | null | undefined,
  locale: string | undefined | null,
): SubtitleSegment[] {
  const input = text == null ? '' : String(text);
  if (!input) return [];

  const segmenter = createSegmenter(locale);
  if (!segmenter) {
    return fallbackSegments(input).map(({ text: t, isWordLike }) => ({ text: t, isWordLike }));
  }

  const out: SubtitleSegment[] = [];
  for (const { segment, isWordLike } of segmenter.segment(input)) {
    out.push({ text: segment, isWordLike: Boolean(isWordLike) });
  }
  return out;
}

function segmentSubtitleTextWithOffsets(
  text: string,
  locale: string | undefined | null,
): SubtitleSegmentWithOffsets[] {
  const segmenter = createSegmenter(locale);
  if (!segmenter) {
    return fallbackSegments(text);
  }

  const out: SubtitleSegmentWithOffsets[] = [];
  for (const { segment, isWordLike, index } of segmenter.segment(text)) {
    out.push({
      text: segment,
      isWordLike: Boolean(isWordLike),
      start: index,
      end: index + segment.length,
    });
  }
  return out;
}

/** Join an inclusive segment index range (preserves gaps/punctuation between). */
export function joinSegmentRange(
  segments: Array<{ text: string; isWordLike?: boolean }>,
  from: number,
  to: number,
): string {
  if (!Array.isArray(segments) || segments.length === 0) return '';
  const start = Math.max(0, Math.min(from, to));
  const end = Math.min(segments.length - 1, Math.max(from, to));
  let out = '';
  for (let i = start; i <= end; i += 1) {
    out += segments[i]?.text ?? '';
  }
  return out;
}

type HtmlAtom = { plain: string; html: string; atomic: boolean };

function expandRubyElement(rubyHtml: string): HtmlAtom[] {
  const open = rubyHtml.match(/^<ruby\b[^>]*>/i);
  const close = rubyHtml.match(/<\/ruby\s*>$/i);
  if (!open || !close) {
    return [{ plain: toPlainSubtitleText(rubyHtml), html: rubyHtml, atomic: true }];
  }
  const inner = rubyHtml.slice(open[0].length, rubyHtml.length - close[0].length);
  const rtCount = (inner.match(/<rt\b/gi) || []).length;
  if (rtCount <= 1) {
    return [{ plain: toPlainSubtitleText(rubyHtml), html: rubyHtml, atomic: true }];
  }

  const pieces: string[] = [];
  const pieceRe = /<rp\b[^>]*>[\s\S]*?<\/rp>|<rt\b[^>]*>[\s\S]*?<\/rt>|[^<]+/gi;
  let m = pieceRe.exec(inner);
  while (m) {
    pieces.push(m[0]);
    m = pieceRe.exec(inner);
  }

  const atoms: HtmlAtom[] = [];
  let i = 0;
  while (i < pieces.length) {
    let base = '';
    while (i < pieces.length && !/^<r[tp]\b/i.test(pieces[i]!)) {
      base += pieces[i];
      i += 1;
    }
    let rpStart = '';
    let rt = '';
    let rpEnd = '';
    if (i < pieces.length && /^<rp\b/i.test(pieces[i]!)) {
      rpStart = pieces[i]!;
      i += 1;
    }
    if (i < pieces.length && /^<rt\b/i.test(pieces[i]!)) {
      rt = pieces[i]!;
      i += 1;
    }
    if (i < pieces.length && /^<rp\b/i.test(pieces[i]!)) {
      rpEnd = pieces[i]!;
      i += 1;
    }
    if (!base && !rt) break;

    const plain = toPlainSubtitleText(base);
    if (!plain) continue;

    if (rt) {
      atoms.push({
        plain,
        html: `<ruby>${base}${rpStart}${rt}${rpEnd}</ruby>`,
        atomic: true,
      });
    } else {
      for (const ch of plain) {
        atoms.push({
          plain: ch,
          html: ch === '\n' ? '<br>' : escapeHtml(ch),
          atomic: false,
        });
      }
    }
  }

  return atoms.length > 0
    ? atoms
    : [{ plain: toPlainSubtitleText(rubyHtml), html: rubyHtml, atomic: true }];
}

function parseSubtitleHtmlAtoms(html: string): HtmlAtom[] {
  const source = html == null ? '' : String(html);
  const atoms: HtmlAtom[] = [];
  const rubyRe = /<ruby\b[^>]*>[\s\S]*?<\/ruby>/gi;
  let last = 0;
  let match = rubyRe.exec(source);

  const pushPlainHtml = (chunk: string) => {
    if (!chunk) return;
    const normalized = chunk.replace(/<br\s*\/?>/gi, '\n');
    const plain = toPlainSubtitleText(normalized);
    for (const ch of plain) {
      atoms.push({
        plain: ch,
        html: ch === '\n' ? '<br>' : escapeHtml(ch),
        atomic: false,
      });
    }
  };

  while (match) {
    if (match.index > last) {
      pushPlainHtml(source.slice(last, match.index));
    }
    for (const atom of expandRubyElement(match[0])) {
      atoms.push(atom);
    }
    last = match.index + match[0].length;
    match = rubyRe.exec(source);
  }
  if (last < source.length) {
    pushPlainHtml(source.slice(last));
  }
  return atoms;
}

type AtomRange = {
  start: number;
  end: number;
  atom: HtmlAtom;
  atomic: boolean;
};

function snapSegmentsToAtomicAtoms(
  segments: SubtitleSegmentWithOffsets[],
  atomRanges: AtomRange[],
  fullPlain: string,
): SubtitleSegmentWithOffsets[] {
  const expanded = segments.map((seg) => {
    let { start, end, isWordLike } = seg;
    for (const ar of atomRanges) {
      if (!ar.atomic) continue;
      if (ar.start < end && ar.end > start) {
        start = Math.min(start, ar.start);
        end = Math.max(end, ar.end);
      }
    }
    return {
      start,
      end,
      isWordLike,
      text: fullPlain.slice(start, end),
    };
  });

  const merged: SubtitleSegmentWithOffsets[] = [];
  for (const seg of expanded) {
    const last = merged[merged.length - 1];
    if (last && seg.start < last.end) {
      last.start = Math.min(last.start, seg.start);
      last.end = Math.max(last.end, seg.end);
      last.isWordLike = last.isWordLike || seg.isWordLike;
      last.text = fullPlain.slice(last.start, last.end);
    } else {
      merged.push({ ...seg });
    }
  }
  return merged;
}

function htmlPlainForRange(atomRanges: AtomRange[], start: number, end: number): { html: string; plain: string } {
  let html = '';
  let plain = '';
  for (const ar of atomRanges) {
    if (ar.end <= start || ar.start >= end) continue;
    if (ar.atom.atomic) {
      html += ar.atom.html;
      plain += ar.atom.plain;
      continue;
    }
    const from = Math.max(start, ar.start) - ar.start;
    const to = Math.min(end, ar.end) - ar.start;
    const part = ar.atom.plain.slice(from, to);
    plain += part;
    for (const ch of part) {
      html += ch === '\n' ? '<br>' : escapeHtml(ch);
    }
  }
  return { html, plain };
}

export function segmentSubtitleHtml(
  html: string | null | undefined,
  locale: string | undefined | null,
  options?: { segmentText?: string } | string | null,
): SubtitleHtmlSegment[] {
  const source = html == null ? '' : String(html);
  if (!source) return [];

  const opts =
    typeof options === 'string' || options == null
      ? { segmentText: options == null ? undefined : String(options) }
      : options;
  const segmentPlain =
    opts.segmentText != null && String(opts.segmentText).length > 0
      ? toPlainSubtitleText(opts.segmentText)
      : toPlainSubtitleText(source);

  if (!/<ruby\b/i.test(source)) {
    return segmentSubtitleText(segmentPlain, locale).map((seg) => ({
      text: seg.text,
      plain: seg.text,
      html: escapeHtml(seg.text).replace(/\n/g, '<br>'),
      isWordLike: seg.isWordLike,
    }));
  }

  const atoms = parseSubtitleHtmlAtoms(source);
  if (atoms.length === 0) return [];

  const atomRanges: AtomRange[] = [];
  let offset = 0;
  for (const atom of atoms) {
    const start = offset;
    offset += atom.plain.length;
    atomRanges.push({ start, end: offset, atom, atomic: atom.atomic });
  }
  const displayPlain = atoms.map((a) => a.plain).join('');

  const plainForSegment =
    segmentPlain.length > 0 && segmentPlain === displayPlain
      ? segmentPlain
      : segmentPlain.length > 0
        ? segmentPlain
        : displayPlain;

  if (plainForSegment !== displayPlain) {
    return segmentSubtitleText(plainForSegment, locale).map((seg) => ({
      text: seg.text,
      plain: seg.text,
      html: escapeHtml(seg.text).replace(/\n/g, '<br>'),
      isWordLike: seg.isWordLike,
    }));
  }

  const rawSegs = segmentSubtitleTextWithOffsets(plainForSegment, locale);
  const snapped = snapSegmentsToAtomicAtoms(rawSegs, atomRanges, plainForSegment);

  return snapped.map((seg) => {
    const { html: pieceHtml, plain } = htmlPlainForRange(atomRanges, seg.start, seg.end);
    return {
      text: plain,
      plain,
      html: pieceHtml,
      isWordLike: seg.isWordLike,
    };
  });
}
